import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { motion } from "framer-motion";
import formIllustration from "../../assets/form.png";

const formSchema = z.object({
  user_name: z.string().min(3, "Mínimo de 3 caracteres"),
  user_phone: z.string().min(11, "Telefone inválido"),
});

type FormSchema = z.infer<typeof formSchema>;
type SectionProps = { scrollToSection?: (_: String) => void };

const Form = forwardRef<HTMLDivElement, SectionProps>((_, ref) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormSchema>({ resolver: zodResolver(formSchema) });

  const handleSendMessage = (data: FormSchema) => {
    const message = `Olá! Meu nome é ${data.user_name} e meu WhatsApp é ${data.user_phone}. Gostaria de saber mais sobre o Cowonder!`;
    const whatsappUrl = `https://wa.me/5581981159904?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    toast.success("Redirecionando para o WhatsApp...");
    reset();
  };

  const formatPhone = (value: string) =>
    value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .slice(0, 15);

  return (
    <section
      ref={ref}
      id="contact"
      className="relative min-h-screen bg-gradient-to-br from-[#f5f0ff] via-white to-[#ede4ff] flex items-center justify-center px-6 py-24 overflow-hidden"
    >
      {/* Blobs decorativos */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#cb88f9] opacity-10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[400px] h-[400px] rounded-full bg-[#490f70] opacity-10 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

        {/* Esquerda */}
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          viewport={{ once: true }}
          className="flex flex-col items-center lg:items-start text-center lg:text-left"
        >
          <span className="inline-block text-[#791cb8] text-xs font-bold uppercase tracking-widest bg-[#791cb8]/10 px-4 py-1.5 rounded-full mb-6">
            Fale com a gente
          </span>
          <h2 className="text-[#1a0533] font-extrabold text-4xl md:text-5xl leading-tight mb-5">
            Vamos transformar<br />
            <span className="text-[#791cb8]">seu negócio?</span>
          </h2>
          <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md">
            Deixe seu contato e nossa equipe entra em contato pelo WhatsApp em até 24 horas — sem compromisso.
          </p>
          <img src={formIllustration} alt="Equipe pronta para atender" className="w-full max-w-xs drop-shadow-xl" />
        </motion.div>

        {/* Direita — card */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.15, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="relative bg-white rounded-3xl shadow-[0_20px_60px_rgba(73,15,112,0.12)] p-10 border border-[#ede4ff]">
            {/* Tarja topo */}
            <div className="absolute inset-x-0 top-0 h-1.5 rounded-t-3xl bg-gradient-to-r from-[#490f70] via-[#791cb8] to-[#cb88f9]" />

            <h3 className="text-[#1a0533] font-extrabold text-2xl mb-1 mt-2">Entre em contato</h3>
            <p className="text-gray-400 text-sm mb-8">Preencha abaixo e fale direto no WhatsApp</p>

            <form onSubmit={handleSubmit(handleSendMessage)} className="flex flex-col gap-6">
              {/* Nome */}
              <div className="flex flex-col gap-2">
                <label className="text-[#490f70] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <PersonOutlineIcon sx={{ fontSize: 14 }} /> Nome completo
                </label>
                <input
                  type="text"
                  placeholder="João Silva"
                  {...register("user_name")}
                  className={`w-full px-4 py-4 rounded-xl border-[1.5px] bg-[#faf8ff] text-[#1a0533] text-sm outline-none transition-all placeholder:text-gray-300 focus:border-[#791cb8] focus:bg-white focus:shadow-[0_0_0_4px_rgba(121,28,184,0.1)] ${errors.user_name ? "border-red-400" : "border-[#e8dff7]"}`}
                />
                {errors.user_name && (
                  <p className="text-red-500 text-xs flex items-center gap-1">⚠ {errors.user_name.message}</p>
                )}
              </div>

              {/* WhatsApp */}
              <div className="flex flex-col gap-2">
                <label className="text-[#490f70] text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                  <PhoneIphoneIcon sx={{ fontSize: 14 }} /> WhatsApp
                </label>
                <input
                  type="text"
                  placeholder="(81) 99999-9999"
                  {...register("user_phone")}
                  onChange={(e) => { e.target.value = formatPhone(e.target.value); }}
                  className={`w-full px-4 py-4 rounded-xl border-[1.5px] bg-[#faf8ff] text-[#1a0533] text-sm outline-none transition-all placeholder:text-gray-300 focus:border-[#791cb8] focus:bg-white focus:shadow-[0_0_0_4px_rgba(121,28,184,0.1)] ${errors.user_phone ? "border-red-400" : "border-[#e8dff7]"}`}
                />
                {errors.user_phone && (
                  <p className="text-red-500 text-xs flex items-center gap-1">⚠ {errors.user_phone.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-2 py-4 rounded-xl bg-gradient-to-r from-[#490f70] to-[#791cb8] text-white font-bold text-sm flex items-center justify-center gap-2.5 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 transition-all shadow-[0_8px_24px_rgba(73,15,112,0.35)]"
              >
                <WhatsAppIcon sx={{ fontSize: 20 }} />
                {isSubmitting ? "Abrindo WhatsApp..." : "Falar no WhatsApp"}
              </button>

              <p className="text-center text-gray-400 text-xs">
                Sem spam. Respondemos em até <span className="text-[#791cb8] font-semibold">24 horas</span>.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
});

export default Form;